//@ts-strict-ignore
import { useMutation } from '@apollo/client';
//Currently only used for delivery methods, keeping naming open-ended in anticipation of future expansion of feature.
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { SalesWorkspaceActions } from './SalesOrderWorkspaceAction';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import React, { ReactElement, useContext, useState } from 'react';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import * as entities from '../../graphql/entities';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import Fieldset from '../../components/connectedForm/Fieldset'; //Circular - But should be okay as a self-contained/small loop.
import SalesOrderAmendMutation from '../../graphql/mutations/sales/SalesOrderAmendMutation.graphql';
import {
  AlertType,
  ISalesOrderAmendMutationResult,
  ISalesOrderAmendMutationVariables,
} from '../../graphql/schema/p4types';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useActionVariables } from '../../utils/useActionProps';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import { serviceActions } from '../../state/modules/service';
import { useDispatch } from 'react-redux';
import { WorkspaceActionHeader } from '../../components/ActionButtons/ActionHeader';
import { BasicHeader } from '../BasicHeader';
import FluentModal from '../../components/modals/FluentModal';

const GlobalAmendModal = ({ modalOpen, setModalOpen }) => {
  const dispatch = useDispatch();
  const { reloadData, selectionState } = useContext(WorkspaceContext);
  const [deliveryMethod, setDeliveryMethod] = useState(null);
  const handleQueryError = useComponentQueryErrorHandling();

  const actionVariables = useActionVariables('salesOrders', {
    salesOrderAmendment: {
      deliveryMethod: normalizeVariables(deliveryMethod),
    },
  });

  const [amend] = useMutation<ISalesOrderAmendMutationResult, ISalesOrderAmendMutationVariables>(
    SalesOrderAmendMutation,
    {
      onError: handleQueryError,
      variables: actionVariables,
    }
  );

  const startAmendmentBackgroundProcess = useBackgroundProcess({
    title: 'Update Delivery Method',
    onComplete: (job) => {
      if (job.recordsProcessed > 0) {
        dispatch(
          serviceActions.genericAlert(
            'Amendment Success',
            `${job.recordsProcessed} sales orders/quotes were amended.`,
            AlertType.Success
          )
        );
      } else {
        dispatch(
          serviceActions.genericAlert(
            'Amendment Failed',
            `1 or more selected sales orders/quotes could not be saved.\n${job.message}`,
            AlertType.Warning
          )
        );
      }
    },
  });

  const onClick = () => {
    amend().then((result) => {
      startAmendmentBackgroundProcess(result.data?.salesOrderGlobalAmend?.result as IJobMeta);
      setModalOpen(false);
    });
    reloadData?.();
  };

  return (
    <FluentModal isOpen={modalOpen}>
      <BasicHeader>
        {'Update Delivery Method'}
        {selectionState.count > 1 ? ` (${selectionState.count} Sales Orders/Quotes)` : ` (1 Sales Order/Quote)`}
      </BasicHeader>
      <Fieldset cols={1}>
        <QueryDropdown
          label={'Delivery Method'}
          {...entities.DeliveryMethod.dropdown}
          onChange={setDeliveryMethod}
          value={deliveryMethod}
          renderInBody={false}
        />
      </Fieldset>
      <ModalBottomButtonContainer>
        <Button data-testid="globalAmendModal-cancel" onClick={() => setModalOpen(false)}>
          Cancel
        </Button>
        <SuccessButton data-testid="globalAmendModal-amend" onClick={onClick}>
          Amend
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export const useGlobalAmendAction: () => [IWorkspaceAction, ReactElement] = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const action: IWorkspaceAction = {
    key: 'salesOrderGlobalAmendAction',
    text: 'Update Delivery Method',
    actionReference: SalesWorkspaceActions.SalesOrderGlobalAmend,
    onClick: () => setModalOpen(true),
    header: WorkspaceActionHeader.STATUS_UPDATE,
  };

  return [action, <GlobalAmendModal modalOpen={modalOpen} setModalOpen={setModalOpen} />];
};
